import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useLeague } from '@/contexts/LeagueContext';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import { Trophy, TrendingUp, Shield } from 'lucide-react';
import SeasonAwards from './SeasonAwards';

interface StandingRow {
  team_id: string;
  team_name: string;
  wins: number;
  losses: number;
  ties: number;
  total_points: number;
  win_pct: number;
  streak: string;
  games_back: number;
  is_mine: boolean;
}

const MLBBetaStandings = () => {
  const { activeLeague, teams } = useLeague();
  const { user } = useAuth();
  const [standings, setStandings] = useState<StandingRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!activeLeague) { setLoading(false); return; }

    const fetchStandings = async () => {
      // Get teams from context (already sorted by total_points) + compute from matchups
      const { data: matchups } = await supabase
        .from('matchups')
        .select('home_team_id, away_team_id, home_score, away_score, status, week')
        .eq('league_id', activeLeague.id)
        .order('week');

      // Build records
      const records: Record<string, {
        name: string;
        wins: number;
        losses: number;
        ties: number;
        total: number;
        results: ('W' | 'L' | 'T')[];
      }> = {};

      // Initialize from teams
      for (const t of teams) {
        records[t.id] = { name: t.name, wins: 0, losses: 0, ties: 0, total: 0, results: [] };
      }

      // Process matchups
      for (const m of (matchups || [])) {
        if (!records[m.home_team_id]) records[m.home_team_id] = { name: '', wins: 0, losses: 0, ties: 0, total: 0, results: [] };
        if (!records[m.away_team_id]) records[m.away_team_id] = { name: '', wins: 0, losses: 0, ties: 0, total: 0, results: [] };

        const hs = Number(m.home_score);
        const as_ = Number(m.away_score);

        records[m.home_team_id].total += hs;
        records[m.away_team_id].total += as_;

        if (m.status === 'final') {
          if (hs > as_) {
            records[m.home_team_id].wins++;
            records[m.away_team_id].losses++;
            records[m.home_team_id].results.push('W');
            records[m.away_team_id].results.push('L');
          } else if (as_ > hs) {
            records[m.away_team_id].wins++;
            records[m.home_team_id].losses++;
            records[m.away_team_id].results.push('W');
            records[m.home_team_id].results.push('L');
          } else {
            records[m.home_team_id].ties++;
            records[m.away_team_id].ties++;
            records[m.home_team_id].results.push('T');
            records[m.away_team_id].results.push('T');
          }
        }
      }

      const rows = Object.entries(records).map(([id, r]) => {
        const gp = r.wins + r.losses + r.ties;
        return {
          team_id: id,
          team_name: r.name,
          wins: r.wins,
          losses: r.losses,
          ties: r.ties,
          total_points: Math.round(r.total * 10) / 10,
          win_pct: gp > 0 ? r.wins / gp : 0,
          streak: computeStreak(r.results),
          games_back: 0,
          is_mine: teams.find(t => t.id === id)?.user_id === user?.id,
        };
      }).sort((a, b) => b.wins - a.wins || b.win_pct - a.win_pct || b.total_points - a.total_points);

      // Calculate games back
      if (rows.length > 0) {
        const leaderWins = rows[0].wins;
        const leaderLosses = rows[0].losses;
        rows.forEach(r => {
          r.games_back = ((leaderWins - r.wins) + (r.losses - leaderLosses)) / 2;
        });
      }

      setStandings(rows);
      setLoading(false);
    };

    fetchStandings();
  }, [activeLeague, teams, user]);

  if (loading) {
    return (
      <div className="space-y-3 pb-20 md:pb-0">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (standings.length === 0) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="p-8 text-center text-muted-foreground">
          <p className="font-medium text-foreground">No standings yet</p>
          <p className="text-sm mt-1">The season hasn't started or no matchups have been scored.</p>
        </CardContent>
      </Card>
    );
  }

  const playoffTeams = activeLeague?.playoff_teams || 4;

  return (
    <div className="space-y-4 pb-20 md:pb-0">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold font-heading text-foreground flex items-center gap-2">
          <Trophy className="w-5 h-5 text-primary" />
          Standings
        </h1>
        <Badge variant="outline" className="text-xs">{standings.length} teams</Badge>
      </div>

      <Card className="bg-card border-border overflow-hidden">
        <div className="overflow-x-auto -mx-0">
          <table className="w-full text-sm table-fixed">
            <thead>
              <tr className="bg-muted/50 text-muted-foreground text-xs">
                <th className="text-left px-2 sm:px-3 py-2.5 w-10">#</th>
                <th className="text-left px-2 sm:px-3 py-2.5 w-auto">Team</th>
                <th className="text-center px-1 sm:px-2 py-2.5 w-8 sm:w-10">W</th>
                <th className="text-center px-1 sm:px-2 py-2.5 w-8 sm:w-10">L</th>
                <th className="text-center px-1 sm:px-2 py-2.5 w-8 sm:w-10">T</th>
                <th className="text-center px-2 py-2.5 hidden sm:table-cell w-14">PCT</th>
                <th className="text-center px-2 py-2.5 hidden sm:table-cell w-12">GB</th>
                <th className="text-center px-2 py-2.5 hidden md:table-cell w-16">Streak</th>
                <th className="text-right pl-2 pr-3 sm:pr-4 py-2.5 min-w-[8ch] tabular-nums">TP</th>
              </tr>
            </thead>
            <tbody>
              {standings.map((s, i) => {
                const isPlayoffSpot = i < playoffTeams;
                const isPlayoffBorder = i === playoffTeams - 1;

                return (
                  <tr
                    key={s.team_id}
                    className={cn(
                      "border-t border-border/50 transition-colors",
                      s.is_mine && "bg-primary/5",
                      isPlayoffBorder && "border-b-2 border-b-primary/30",
                    )}
                  >
                    <td className="px-2 sm:px-3 py-3">
                      <div className="flex items-center gap-1">
                        <span className="font-bold text-muted-foreground">{i + 1}</span>
                        {isPlayoffSpot && (
                          <Shield className="w-3 h-3 text-primary shrink-0" />
                        )}
                      </div>
                    </td>
                    <td className="px-2 sm:px-3 py-3 max-w-0">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span className={cn("font-medium truncate", s.is_mine ? "text-primary" : "text-foreground")}>
                          {s.team_name}
                        </span>
                        {s.is_mine && (
                          <Badge variant="secondary" className="text-[9px] px-1 h-4 shrink-0">YOU</Badge>
                        )}
                      </div>
                    </td>
                    <td className="text-center px-1 sm:px-2 py-3 font-semibold text-foreground">{s.wins}</td>
                    <td className="text-center px-1 sm:px-2 py-3 font-semibold text-muted-foreground">{s.losses}</td>
                    <td className="text-center px-1 sm:px-2 py-3 text-muted-foreground">{s.ties || '-'}</td>
                    <td className="text-center px-2 py-3 hidden sm:table-cell text-muted-foreground">
                      {s.win_pct.toFixed(3).replace(/^0/, '')}
                    </td>
                    <td className="text-center px-2 py-3 hidden sm:table-cell text-muted-foreground">
                      {i === 0 ? '-' : s.games_back.toFixed(1)}
                    </td>
                    <td className="text-center px-2 py-3 hidden md:table-cell">
                      {s.streak ? (
                        <Badge
                          variant="outline"
                          className={cn(
                            "text-[10px] px-1.5",
                            s.streak.startsWith('W') && "border-primary/40 text-primary",
                            s.streak.startsWith('L') && "border-destructive/40 text-destructive",
                          )}
                        >
                          {s.streak}
                        </Badge>
                      ) : '-'}
                    </td>
                    <td className="text-right pl-2 pr-3 sm:pr-4 py-3 font-bold text-primary tabular-nums whitespace-nowrap">{s.total_points.toFixed(1)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Playoff cutline legend */}
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Shield className="w-3 h-3 text-primary" />
        <span>Top {playoffTeams} teams make the playoffs</span>
      </div>

      {/* Season Awards */}
      <SeasonAwards standings={standings} />
    </div>
  );
};

function computeStreak(results: ('W' | 'L' | 'T')[]): string {
  if (results.length === 0) return '';
  const last = results[results.length - 1];
  let count = 0;
  for (let i = results.length - 1; i >= 0; i--) {
    if (results[i] === last) count++;
    else break;
  }
  return `${last}${count}`;
}

export default MLBBetaStandings;
